import { Button } from 'antd';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import {
  loadBilledByDay,
  loadBilledByMonth,
  loadDuplicates,
  loadDuplicatesByDay,
  loadOpenOrderStatus,
  loadOpenOrderStatusBySku,
  loadPaidByMonth
} from '../../../store/ducks/dashboard/actions';
import { IDashboardState } from '../../../store/ducks/dashboard/types';
import {
  ECustomerTypeView,
  IUserPermissions
} from '../../../store/ducks/user/types';
import BilledByDay from './BilledByDay';
import BilledByMonth from './BilledByMonth';
import Duplicates from './Duplicates';
import DuplicatesByDay from './DuplicatesByDay';
import OpenOrderStatus from './OpenOrderStatus';
import PaymentsByMonth from './PaymentsByMonth';
import {
  DashboardContainer,
  ErrorIcon,
  ItemContainerError,
  TryAgainIcon
} from '../styles';
import moment from 'moment';
import { sendTagging } from '../../../store/ducks/tagging/actions';

const LoadingSkeleton = () => {
  return <Skeleton count={5} width={'98.5%'} style={{ margin: '0.5rem 0' }} />;
};

const ErrorMessageBlock: React.FC<{
  title: string;
  onTryAgainClick(): void;
}> = ({ title, onTryAgainClick }) => {
  return (
    <ItemContainerError>
      <ErrorIcon />
      <span>
        {title} <br />
        Por favor, tente novamente mais tarde.
      </span>

      <Button onClick={onTryAgainClick} className="try-again">
        <TryAgainIcon />
        Tentar novamente
      </Button>
    </ItemContainerError>
  );
};

interface IDashboardContentProps {
  dashboardState: IDashboardState;
  userPermissions: IUserPermissions;
  viewOpenOrderStatusBySku(): void;
  customerType: ECustomerTypeView;
}

const DashboardContent: React.FC<IDashboardContentProps> = ({
  dashboardState,
  userPermissions,
  viewOpenOrderStatusBySku,
  customerType
}) => {
  const dispatch = useDispatch();

  const onSkuDetailClick = () => {
    if (!dashboardState.openOrderStatus?.dashboardBySku) {
      dispatch(
        loadOpenOrderStatusBySku(
          dashboardState.selectedCustomerId,
          customerType
        )
      );
      dispatch(
        sendTagging({
          screenName: 'LG:PainelGestao:Selecao',
          itemDescription: `painelGestao_verVolumeSku`,
          itemName: `button:volumeSku`,
          event: 'click'
        })
      );
    }

    viewOpenOrderStatusBySku();
    document.documentElement.scrollTop = 0;
  };

  return (
    <>
      {dashboardState.openOrderStatus && (
        <OpenOrderStatus
          openOrderStatusState={dashboardState.openOrderStatus}
          skuDetailClick={onSkuDetailClick}
          errorMessageBlock={
            <ErrorMessageBlock
              title="Falha ao carregar os pedidos."
              onTryAgainClick={() =>
                dispatch(
                  loadOpenOrderStatus(
                    dashboardState.selectedCustomerId,
                    customerType
                  )
                )
              }
            />
          }
        >
          <LoadingSkeleton />
        </OpenOrderStatus>
      )}
      <DashboardContainer>
        <div
          className={`squared-charts 
                        ${
                          userPermissions.hasOrderViewAccess &&
                          !userPermissions.hasFinancialAccess
                            ? 'only-orders'
                            : !userPermissions.hasOrderViewAccess &&
                              userPermissions.hasFinancialAccess
                            ? 'only-financial'
                            : ''
                        }`}
        >
          {dashboardState.billedByMonth && (
            <BilledByMonth
              billedByMonthState={dashboardState.billedByMonth}
              errorMessageBlock={
                <ErrorMessageBlock
                  title="Falha ao carregar os itens faturados."
                  onTryAgainClick={() =>
                    dispatch(
                      loadBilledByMonth(
                        dashboardState.selectedCustomerId,
                        customerType
                      )
                    )
                  }
                />
              }
            >
              <LoadingSkeleton />
            </BilledByMonth>
          )}

          {dashboardState.paidByMonth && (
            <PaymentsByMonth
              paidByMonthState={dashboardState.paidByMonth}
              errorMessageBlock={
                <ErrorMessageBlock
                  title="Falha ao carregar os pagamentos."
                  onTryAgainClick={() =>
                    dispatch(
                      loadPaidByMonth(
                        dashboardState.selectedCustomerId,
                        customerType
                      )
                    )
                  }
                />
              }
            >
              <LoadingSkeleton />
            </PaymentsByMonth>
          )}

          {dashboardState.duplicates && (
            <Duplicates
              duplicatesState={dashboardState.duplicates}
              errorMessageBlock={
                <ErrorMessageBlock
                  title="Falha ao carregar as duplicatas."
                  onTryAgainClick={() =>
                    dispatch(
                      loadDuplicates(
                        dashboardState.selectedCustomerId,
                        customerType
                      )
                    )
                  }
                />
              }
            >
              <LoadingSkeleton />
            </Duplicates>
          )}
        </div>
        <div className="bargraphs">
          {dashboardState.billedByDay && (
            <BilledByDay
              billedByDayState={dashboardState.billedByDay}
              onChangeMonth={(selectedMonth: moment.Moment) =>
                dispatch(
                  loadBilledByDay(
                    dashboardState.selectedCustomerId,
                    customerType,
                    selectedMonth
                  )
                )
              }
              errorMessageBlock={
                <ErrorMessageBlock
                  title="Falha ao carregar itens faturados por dia."
                  onTryAgainClick={() =>
                    dispatch(
                      loadBilledByDay(
                        dashboardState.selectedCustomerId,
                        customerType,
                        dashboardState.billedByDay?.monthDate
                      )
                    )
                  }
                />
              }
            >
              <LoadingSkeleton />
            </BilledByDay>
          )}
          {dashboardState.duplicatesByDay && (
            <DuplicatesByDay
              duplicatesByDayState={dashboardState.duplicatesByDay}
              onChangeMonth={(selectedMonth: moment.Moment) =>
                dispatch(
                  loadDuplicatesByDay(
                    dashboardState.selectedCustomerId,
                    customerType,
                    selectedMonth
                  )
                )
              }
              errorMessageBlock={
                <ErrorMessageBlock
                  title="Falha ao carregar as duplicatas."
                  onTryAgainClick={() =>
                    dispatch(
                      loadDuplicatesByDay(
                        dashboardState.selectedCustomerId,
                        customerType,
                        dashboardState.duplicatesByDay?.monthDate
                      )
                    )
                  }
                />
              }
            >
              <LoadingSkeleton />
            </DuplicatesByDay>
          )}
        </div>
      </DashboardContainer>
    </>
  );
};

export default DashboardContent;
